import dayjs from "dayjs";
import moment from "moment";
import Button from "../../Button";
import constants from "../../../constants";
import Radio from "../../CommonComponents/Radio";
import AlertContext from "../../../context/AlertContext";
import InputField from "../../CommonComponents/InputField";
import Datepicker from "../../CommonComponents/Datepicker";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import useAxiosPrivate from "../../../Hooks/useAxiosPrivate";
import React, { useState, useContext, useEffect, useRef } from "react";
import DragAndDropUpload from "../../CommonComponents/DragAndDropUpload";
import useS3FileManagement from "../../../Hooks/useS3FileManagement";
import RichTextEditor from "../../CommonComponents/RichTextEditor";
import ToggleButton from "../../CommonComponents/ToggleButton";
import InputDropdown from "../../CommonComponents/InputDropdown";
import Toast from "../../CommonComponents/Toast";
import Loading from "../../Notification-Loading/Loading/Loading";

const validators = {
  name: (v) =>
    !v?.trim()
      ? "Designation is required"
      : v?.length >= 100
      ? "Max characters: 100"
      : "",
  company: (v) =>
    !v?.trim()
      ? "Company name is required"
      : v?.length >= 100
      ? "Max characters: 100"
      : "",
  skills: (v) =>
    !v?.trim()
      ? "Skills are required"
      : v?.length >= 1000
      ? "Max characters: 1000"
      : "",
  logo: (v) => (!v ? "Logo is required" : ""),
  minexp: (v, jobType) =>
    jobType === 1 &&
    (!Number.isInteger(+v)
      ? "Minimum experience is required"
      : v < 0
      ? "Minimum experience cannot be negative"
      : ""),
  maxexp: (v, jobType) =>
    jobType === 1 &&
    (!v || !Number.isInteger(+v)
      ? "Maximum experience is required"
      : v < 0
      ? "Maximum experience cannot be negative"
      : ""),
  passedOut: (v, jobType) =>
    jobType === 0 && (!v || !v.length ? "Passing out year is required" : ""),
  duration: (v, jobType) =>
    jobType === 0 &&
    (!v || !Number.isInteger(+v)
      ? "Duration is required"
      : v <= 0
      ? "Duration must be greater than 0"
      : ""),
  minctc: (v) =>
    v && !Number.isInteger(+v)
      ? "Minimum CTC must be a valid number"
      : v && v <= 0
      ? "Minimum CTC must be greater than 0"
      : "",
  maxctc: (v) =>
    v && !Number.isInteger(+v)
      ? "Maximum CTC must be a valid number"
      : v && v <= 0
      ? "Maximum CTC must be greater than 0"
      : v && v > 5000000
      ? "Maximum CTC must be greater than 50 LPA"
      : "",
  noofopenings: (v) =>
    v && !Number.isInteger(+v)
      ? "Openings must be a valid number"
      : v && v <= 0
      ? "Openings must be greater than 0"
      : "",
};

const supportedFormat = ["jpg", "jpeg", "png"];

export default function JobForm({ openForm, setOpenForm }) {
  const Alert = useContext(AlertContext);

  const axios = useAxiosPrivate();
  const { onUploadS3Object, getS3FileAsFile } = useS3FileManagement();

  const editorRef = useRef(null);

  const [selectedQualifications] = useState([]);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("success");

  const {
    currentEditJob,
    isEditJob,
    setIsEditJob,
    setShowNotify,
    isLoaded,
    setIsLoaded,
  } = useContext(AlertContext);

  const [locationOptions] = useState([
    { label: "Gurugram", value: 10 },
    { label: "Mumbai", value: 9 },
    { label: "Pune", value: 8 },
    { label: "Delhi", value: 4 },
    { label: "Noida", value: 7 },
    { label: "Chennai", value: 5 },
    { label: "Kolkata", value: 6 },
    { label: "Hyderabad", value: 1 },
    { label: "Bengaluru", value: 3 },
    { label: "Visakhapatnam", value: 2 },
  ]);

  const [jobInfo, setjobInfo] = useState(
    isEditJob
      ? {}
      : {
          type: 1,
          link: "",
          logo: "",
          isjob: 1,
          name: "",
          jobid: "",
          minexp: "",
          maxexp: "",
          skills: "",
          minctc: null,
          maxctc: null,
          workmode: 1,
          company: "",
          duration: "",
          location: [],
          paymentMode: 0,
          posteddate: "",
          startdate: null,
          description: "",
          qualification: "",
          noofopenings: null,
        },
  );
  const [errors, setErrors] = useState({});
  const [isPosting, setIsPosting] = useState(false);
  const [isRemoved, setIsRemoved] = useState(false);

  const currentYear = new Date().getFullYear() - 5;

  const years = Array.from({ length: 8 }, (_, index) => ({
    value: currentYear + index,
    label: `${currentYear + index}`,
  }));

  useEffect(() => {
    if (isEditJob) {
      const updateJobInfo = async () => {
        setIsLoaded(true);
        let copyJobInfo = { ...currentEditJob };

        copyJobInfo.location =
          copyJobInfo.location?.split(",")?.map(Number) || [];
        if (copyJobInfo?.logo && copyJobInfo?.logo !== "") {
          const result = await getS3FileAsFile(copyJobInfo.logo, "HOST");
          console.log("result", result);

          copyJobInfo.logo = result || null;
        }
        copyJobInfo.passedOut = copyJobInfo.PassingOutYear
          ? copyJobInfo.PassingOutYear?.trim()?.split(",")?.map(Number) || []
          : [];

        setjobInfo(copyJobInfo);
        setIsLoaded(false);
      };
      updateJobInfo();
    }
  }, [isEditJob]);

  const toggleDrawer = (anchor, open) => (event) => {
    if (
      event &&
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }

    setOpenForm(open);
  };

  const handleChange = (field, value) => {
    let validate = validators[field];
    if (validate) {
      setErrors((prev) => ({
        ...prev,
        [field]: validate(value, jobInfo.isjob),
      }));
    }
    if (field === "startdate") {
      let updatedValue = value;
      if (value && typeof value === "object" && value.$d) {
        updatedValue = dayjs(value).format("YYYY-MM-DD");
      }
      setjobInfo({ ...jobInfo, [field]: updatedValue });
    } else setjobInfo({ ...jobInfo, [field]: value });
  };

  const onDragOver = (event) => event.preventDefault();

  const onDrop = (event) => {
    const files = Array.from(event.dataTransfer.files);

    uploadFiles(files);
  };

  const uploadFiles = (files = []) => {
    if (files.length > 1) {
      setToastOpen(true);
      setToastMessage("Multiple files are not allowed.");
      setToastType("warning");
      return;
    }

    const isUploadableFormat = files.every((file) =>
      supportedFormat.includes(file.type.split("/")[1]),
    );

    if (!isUploadableFormat) return;
    let validate = validators.logo;
    if (validate) {
      setErrors((prev) => ({
        ...prev,
        logo: validate(files[0], jobInfo.isjob),
      }));
    }

    setjobInfo({ ...jobInfo, logo: files[0] });
  };

  const onRemovelogo = () => {
    if (isEditJob) setIsRemoved(true);
    setErrors((prev) => ({ ...prev, logo: "Logo is required" }));
    setjobInfo({ ...jobInfo, logo: null });
  };

  const onUpload = (event) => {
    const files = Array.from(event.target.files);

    uploadFiles(files);
    event.target.value = "";
  };

  const handlePostJob = async (e) => {
    e.preventDefault();

    try {
      const payload = { ...jobInfo };
      setIsPosting(true);
      payload.qualification =
        selectedQualifications.length > 0
          ? selectedQualifications.join(",")
          : null;
      payload.location = payload.location?.length
        ? payload.location.join(",")
        : null;
      let errors = {};
      Object.keys(validators).forEach((field) => {
        let validate = validators[field];
        if (validate) {
          const error = validate(payload[field], payload.isjob);
          if (error) {
            errors[field] = error;
          }
        }
      });

      setErrors(errors);
      if (Object.keys(errors).length > 0) {
        setToastOpen(true);
        setToastMessage("Please fill the highlighted fields");
        setToastType("warning");
        setIsPosting(false);
        return;
      }
      if (
        payload.logo &&
        payload.logo !== "" &&
        payload.logo.name !== undefined
      ) {
        let imageName = payload.logo.name,
          path =
            !isEditJob || (isEditJob && isRemoved)
              ? `jobs/logo/${imageName?.substring(
                  0,
                  imageName.lastIndexOf("."),
                )}_${moment().toISOString()}${imageName?.substring(
                  imageName.lastIndexOf("."),
                )}`
              : `jobs/logo/${imageName}`;

        if (!isEditJob || (isEditJob && isRemoved)) {
          await onUploadS3Object(
            path,
            payload.logo,
            imageName?.split(".").pop().toLowerCase(),
            "HOST",
            "PUT",
          );
        }

        payload.logo = path;
      } else {
        if (!isEditJob && payload.logo === "") {
          setToastOpen(true);
          setToastMessage("Please upload logo");
          setToastType("warning");
          return;
        }
      }
      const { data } = isEditJob
        ? await axios.put("node/learner/job", payload)
        : await axios.post("node/learner/job", payload);

      if (data.resultCode === constants.RESULT_STATUS.SUCCESS) {
        setjobInfo({});
        setIsEditJob(false);
        setShowNotify({
          show: true,
          msg: `Job ${isEditJob ? "updated" : "posted"} successfully.`,
          title: "Success",
        });
        Alert.navigate("/jobs");
      } else {
        setToastOpen(true);
        setToastMessage(
          `Unable to ${
            isEditJob ? "update" : "post"
          } the job. Please try again later`,
        );
        setToastType("error");
      }
    } catch (error) {
      console.error("error in post job func", error);
      let isUnAuth = error.message.includes("403");
      if (isUnAuth) {
        setShowNotify({
          show: true,
          msg: "You have been logged-out due to inactivity. Login again.",
          title: "Warning !",
          isUnAuth,
        });
        return;
      }
      setToastOpen(true);
      setToastMessage(
        `Unable to ${
          isEditJob ? "update" : "post"
        } the job. Please try again later`,
      );
      setToastType("error");
    } finally {
      setIsPosting(false);
    }
  };

  console.log("JobInfo:", jobInfo);
  console.log("errors:", errors);

  const buttonText = isEditJob
    ? isPosting
      ? "Updating..."
      : "Update"
    : isPosting
    ? "Adding..."
    : "Add";

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={openForm}
      className="jobFormMainContainer"
      onOpen={toggleDrawer("bottom", true)}
      onClose={toggleDrawer("bottom", false)}
    >
      <div className="jobFormDrawer">
        <div className="jobFormHeaderSec">
          <div className="jobFormHeader">Add Job / Internship</div>
          <div className="jobFormHeaderBtn">
            <Button
              size="sm"
              hierarchy={{ type: "tertiaryGrey", buttonText: "Cancel" }}
              onClick={() => {
                setOpenForm(false);
                Alert.navigate("/jobs");
              }}
            />
            <Button
              size="sm"
              onClick={handlePostJob}
              disable={isPosting}
              hierarchy={{
                type: "primary",
                buttonText: buttonText,
              }}
            />
          </div>
        </div>
        <div className="jobFormContainer">
          <form
            className="jobForm"
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
              }
            }}
          >
            <div className="jobMode">
              <p className="jobModeParagraph">Type</p>
              <div className="jobModeRadioBtn">
                <Radio
                  value={1}
                  size="sm"
                  text="Job"
                  checked={jobInfo.isjob === 1}
                  onChange={() => setjobInfo({ ...jobInfo, isjob: 1 })}
                />
                <Radio
                  size="sm"
                  value={0}
                  text="Internship"
                  checked={jobInfo.isjob === 0}
                  onChange={() => setjobInfo({ ...jobInfo, isjob: 0 })}
                />
              </div>
            </div>
            <InputField
              name="name"
              label="Designation"
              value={jobInfo.name}
              onChange={(e) => handleChange(e.target.name, e.target.value)}
              placeholder="Enter job title"
              hintText={errors.name}
              destructive={!!errors.name}
            />
            <InputField
              name="jobid"
              label="Job Id"
              value={jobInfo.jobid}
              onChange={(e) => handleChange(e.target.name, e.target.value)}
              placeholder="Enter job Id"
              hintText={errors.jobid}
              destructive={!!errors.jobid}
            />
            <InputField
              name="company"
              label="Hiring Company"
              onChange={(e) => handleChange(e.target.name, e.target.value)}
              value={jobInfo.company}
              placeholder="Enter Company name"
              hintText={errors.company}
              destructive={!!errors.company}
            />
            <DragAndDropUpload
              file={jobInfo.logo ?? null}
              onDrop={onDrop}
              onUpload={onUpload}
              onDragOver={onDragOver}
              onRemoveFile={onRemovelogo}
              label="Hiring Company Logo"
              hintText={errors.logo}
              destructive={!!errors.logo}
              supportFormat={".jpg, .jpeg, .png"}
            />
            {jobInfo.isjob ? (
              <div className="jobMode">
                <p className="jobModeParagraph">Job Type</p>
                <div className="jobModeRadioBtn">
                  <Radio
                    size="sm"
                    text="Permanent"
                    onChange={() => setjobInfo({ ...jobInfo, type: 1 })}
                    checked={jobInfo.type === constants.jobtype.Permanent}
                  />
                  <Radio
                    size="sm"
                    text="Contract"
                    onChange={() => setjobInfo({ ...jobInfo, type: 2 })}
                    checked={jobInfo.type === constants.jobtype.Contract}
                  />
                  <Radio
                    size="sm"
                    text="Part Time"
                    onChange={() => setjobInfo({ ...jobInfo, type: 3 })}
                    checked={jobInfo.type === constants.jobtype.PartTime}
                  />
                </div>
              </div>
            ) : null}
            <div className="jobMode">
              <p className="jobModeParagraph">Work Mode </p>
              <div className="jobModeRadioBtn">
                <Radio
                  size="sm"
                  text="Hybrid"
                  checked={jobInfo.workmode === 1}
                  onChange={() => setjobInfo({ ...jobInfo, workmode: 1 })}
                />
                <Radio
                  size="sm"
                  text="Remote"
                  checked={jobInfo.workmode === 2}
                  onChange={() => setjobInfo({ ...jobInfo, workmode: 2 })}
                />
                <Radio
                  size="sm"
                  text="In Office"
                  checked={jobInfo.workmode === 3}
                  onChange={() => setjobInfo({ ...jobInfo, workmode: 3 })}
                />
              </div>
            </div>
            {jobInfo.isjob ? (
              <div className="jobMode jobModeRange">
                <p className="jobModeParagraph">Experience Required:</p>
                <div style={{ display: "flex", gap: "48px" }}>
                  <InputField
                    type="number"
                    name="minexp"
                    placeholder="0"
                    value={jobInfo.minexp}
                    onChange={(e) =>
                      handleChange(e.target.name, e.target.value)
                    }
                    label="Minimum Experience"
                    hintText={errors.minexp}
                    destructive={!!errors.minexp}
                  />
                  <InputField
                    type="number"
                    name="maxexp"
                    placeholder="2"
                    value={jobInfo.maxexp}
                    onChange={(e) =>
                      handleChange(e.target.name, e.target.value)
                    }
                    label="Maximum Experience"
                    hintText={errors.maxexp}
                    destructive={!!errors.maxexp}
                  />
                </div>
              </div>
            ) : null}
            {jobInfo.workmode !== 2 && (
              <InputDropdown
                searchable
                options={locationOptions ?? []}
                value={jobInfo.location ?? []}
                multiple
                name="location"
                placeholder="Choose Locations"
                label={"Location(s)"}
                onChange={(value) => handleChange("location", value)}
              />
            )}
            {!jobInfo.isjob && (
              <InputDropdown
                searchable
                options={years ?? []}
                value={jobInfo.passedOut ?? []}
                multiple
                name="passedOut"
                placeholder={"Choose Passing out year"}
                hintText={errors.passedOut}
                destructive={!!errors.passedOut}
                label={"Applicable Passing out year"}
                onChange={(value) => handleChange("passedOut", value)}
              />
            )}
            {!jobInfo.isjob && (
              <ToggleButton
                text="Paid :"
                pressed={jobInfo.paymentMode}
                name="paymentMode"
                reverse
                onChange={(value) => handleChange("paymentMode", value)}
              />
            )}

            <div className="jobMode jobModeRange">
              <p className="jobModeParagraph">
                {jobInfo.isjob ? "Salary" : "Stipend"} Range:
              </p>
              <div style={{ display: "flex", gap: "48px" }}>
                <InputField
                  name="minctc"
                  label="Minimum CTC"
                  type="number"
                  placeholder="400000"
                  value={jobInfo.minctc}
                  onChange={(e) => handleChange(e.target.name, e.target.value)}
                  hintText={errors.minctc}
                  destructive={!!errors.minctc}
                />
                <InputField
                  name="maxctc"
                  type="number"
                  label="Maximum CTC"
                  placeholder="1200000"
                  value={jobInfo.maxctc}
                  onChange={(e) => handleChange(e.target.name, e.target.value)}
                  hintText={errors.maxctc}
                  destructive={!!errors.maxctc}
                />
              </div>
            </div>

            <InputField
              name="skills"
              placeholder="Skills"
              value={jobInfo.skills}
              label="Skills Required"
              onChange={(e) => handleChange(e.target.name, e.target.value)}
              hintText={errors.skills}
              destructive={!!errors.skills}
            />
            {!jobInfo.isjob && (
              <div className="internshipStartDate">
                <label>Starting Date</label>

                <Datepicker
                  value={jobInfo.startdate ?? null}
                  minDate={dayjs(jobInfo.startdate)}
                  onChange={(value) => handleChange("startdate", value)}
                />
              </div>
            )}
            {!jobInfo.isjob && (
              <InputField
                name="duration"
                onChange={(e) => handleChange(e.target.name, e.target.value)}
                value={jobInfo.duration}
                label="Total Duration of Internship"
                placeholder="Enter Internship Duration in No of Months"
                hintText={errors.duration}
                destructive={!!errors.duration}
                type="number"
              />
            )}
            <InputField
              label="Openings"
              name="noofopenings"
              onChange={(e) => handleChange(e.target.name, e.target.value)}
              value={jobInfo.noofopenings}
              placeholder="Enter  No of Openings"
              hintText={errors.noofopenings}
              destructive={!!errors.noofopenings}
              type="number"
            />
            <InputField
              name="link"
              value={jobInfo.link}
              label="Link to apply"
              onChange={(e) => handleChange(e.target.name, e.target.value)}
              placeholder="Add your Job link here"
              hintText={errors.link}
              destructive={!!errors.link}
            />
            <RichTextEditor
              title="Description"
              value={jobInfo.description}
              onChange={(value) => handleChange("description", value)}
              tinymceScriptSrc="/tinymce/tinymce.min.js"
              onInit={(evt, editor) => (editorRef.current = editor)}
              init={{
                readonly: true,
                menubar: false,
                min_height: 150,
                branding: false,
                statusbar: false,
                license_key: "gpl",
                elementpath: false,
                selector: "textarea",
                allow_html_in_named_anchor: true,
                plugins: [
                  "link",
                  "help",
                  "code",
                  "lists",
                  "image",
                  "table",
                  "media",
                  "anchor",
                  "editor",
                  "advlist",
                  "preview",
                  "charmap",
                  "wordcount",
                  "fullscreen",
                  "autoresize",
                  "visualblocks",
                  "searchreplace",
                  "insertdatetime",
                ],
                toolbar:
                  "undo redo | blocks | " +
                  "bold italic underline forecolor | link | alignleft aligncenter " +
                  "alignright alignjustify | bullist numlist outdent indent | " +
                  "removeformat | help |",
                content_style:
                  "body { font-family:Helvetica,Arial,sans-serif; font-size:14px }",
              }}
            />
          </form>
        </div>
        <Toast
          open={toastOpen}
          message={toastMessage}
          type={toastType}
          onClose={() => setToastOpen(false)}
          autoHideDuration={3000}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        />
        {isLoaded && <Loading />}
      </div>
    </SwipeableDrawer>
  );
}
